
import React, { useState, useEffect } from 'react';
import { auth, database, storage } from '../firebase';
import { ref, push, onValue, set, off } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const BranchProfile = () => {
  const [formData, setFormData] = useState({
    branchId: '',
    branchName: '',
    address1: '',
    address2: '',
    city: '',
    zipCode: '',
    state: '',
    country: '',
    phoneNo: '',
    mobileNo: '',
    purchaseDeptMobileNo: '',
    faxNo: '',
    email: '',
    vatNo: '',
    cstNo: '',
    gstinNo: '',
    dlNo: '',
    remarks: '',
    bankName: '',
    bankBranchName: '',
    accountNo: '',
    ifscCode: '',
    micrCode: '',
    image: null,
    qrCode: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);
      if (user) {
        const encodedEmail = encodeEmailForPath(user.email);
        const branchProfilesRef = ref(database, `users/${encodedEmail}/branchProfiles`);
        const listener = onValue(branchProfilesRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const profiles = Object.entries(data);
            const latestProfile = profiles[profiles.length - 1];
            setProfileId(latestProfile[0]);
            setFormData({
              ...latestProfile[1],
              image: null,
              qrCode: null,
            });
            setImagePreview(latestProfile[1].image || null);
            setQrCodePreview(latestProfile[1].qrCode || null);
            setIsSaved(true);
          } else {
            setIsSaved(false);
            setProfileId(null);
          }
        });
        return () => off(branchProfilesRef, 'value', listener);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size should be less than 5MB');
        return;
      }
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setErrorMessage('Please upload a valid image file (JPEG, PNG, or GIF)');
        return;
      }
      if (type === 'image') {
        setFormData({ ...formData, image: file });
        setImagePreview(URL.createObjectURL(file));
      } else {
        setFormData({ ...formData, qrCode: file });
        setQrCodePreview(URL.createObjectURL(file));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    setSubmitting(true);

    if (!currentUser) {
      setErrorMessage('You must be logged in to save a profile');
      setSubmitting(false);
      return;
    }

    try {
      let imageUrl = formData.image ? '' : formData.image;
      let qrCodeUrl = formData.qrCode ? '' : formData.qrCode;

      if (formData.image) {
        const timestamp = new Date().getTime();
        const filename = `${timestamp}_${formData.image.name}`;
        const fileRef = storageRef(storage, `branchProfiles/${filename}`);
        const uploadResult = await uploadBytes(fileRef, formData.image);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }

      if (formData.qrCode) {
        const timestamp = new Date().getTime();
        const filename = `${timestamp}_${formData.qrCode.name}`;
        const fileRef = storageRef(storage, `branchProfiles/${filename}`);
        const uploadResult = await uploadBytes(fileRef, formData.qrCode);
        qrCodeUrl = await getDownloadURL(uploadResult.ref);
      }

      const profileData = {
        ...formData,
        image: imageUrl,
        qrCode: qrCodeUrl,
        createdAt: isSaved ? formData.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userEmail: currentUser.email,
      };

      delete profileData.image;
      delete profileData.qrCode;

      const encodedEmail = encodeEmailForPath(currentUser.email);
      const branchProfilesRef = ref(database, `users/${encodedEmail}/branchProfiles`);

      if (isSaved && profileId) {
        const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles/${profileId}`);
        await set(branchProfileRef, profileData);
        setSuccessMessage('Profile updated successfully!');
      } else {
        const newProfileRef = await push(branchProfilesRef, profileData);
        setProfileId(newProfileRef.key);
        setSuccessMessage('Profile saved successfully!');
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error saving/updating profile:', error);
      setErrorMessage(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      branchId: '',
      branchName: '',
      address1: '',
      address2: '',
      city: '',
      zipCode: '',
      state: '',
      country: '',
      phoneNo: '',
      mobileNo: '',
      purchaseDeptMobileNo: '',
      faxNo: '',
      email: '',
      vatNo: '',
      cstNo: '',
      gstinNo: '',
      dlNo: '',
      remarks: '',
      bankName: '',
      bankBranchName: '',
      accountNo: '',
      ifscCode: '',
      micrCode: '',
      image: null,
      qrCode: null,
    });
    setImagePreview(null);
    setQrCodePreview(null);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      minHeight: '100vh',
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '2.5rem',
      border: '1px solid #e2e8f0',
    },
    heading: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '2rem',
      color: '#1f2937',
      textAlign: 'center',
    },
    messageSuccess: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    messageError: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
      border: '1px solid #fecaca',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#6b7280',
    },
    loadingSpinner: {
      width: '3rem',
      height: '3rem',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 1rem',
    },
    formLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '2rem',
    },
    twoColumnLayout: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    sectionHeading: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    inputGroup: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    input: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    textarea: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      minHeight: '100px',
      transition: 'border-color 0.2s ease',
    },
    fileInput: {
      display: 'none',
    },
    fileInputLabel: {
      display: 'inline-block',
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: 'white',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
    },
    imagePreview: {
      marginTop: '1rem',
      width: '120px',
      height: '120px',
      objectFit: 'cover',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    imageUploadSection: {
      padding: '1.5rem',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      textAlign: 'center',
      backgroundColor: '#f9fafb',
    },
    buttonContainer: {
      marginTop: '2rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
    },
    button: {
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontWeight: '500',
      fontSize: '0.875rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
    },
    submitButton: {
      backgroundColor: '#10b981',
      color: 'white',
    },
    cancelButton: {
      backgroundColor: '#ef4444',
      color: 'white',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={styles.formWrapper}>
          <div style={styles.loadingContainer}>
            <p>Please login to access the Branch Profile</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.formWrapper}>
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading profile data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <h1 style={styles.heading}>🏢 Branch Profile</h1>

        {successMessage && (
          <div style={styles.messageSuccess}>
            <span>✅</span>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div style={styles.messageError}>
            <span>❌</span>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formLayout}>
            <div style={styles.twoColumnLayout}>
              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>📍 Branch Details</h2>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Branch ID</label>
                  <input
                    type="text"
                    name="branchId"
                    value={formData.branchId}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Branch ID"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Branch Name<span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span></label>
                  <input
                    type="text"
                    name="branchName"
                    value={formData.branchName}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Branch Name"
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Address Line 1</label>
                  <input
                    type="text"
                    name="address1"
                    value={formData.address1}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Address Line 1"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Address Line 2</label>
                  <input
                    type="text"
                    name="address2"
                    value={formData.address2}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Address Line 2"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter City"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>ZIP Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter ZIP Code"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>State</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    style={styles.select}
                  >
                    <option value="">Select State</option>
                    <option value="TAMIL NADU">Tamil Nadu</option>
                    <option value="KERALA">Kerala</option>
                    <option value="KARNATAKA">Karnataka</option>
                    <option value="MAHARASHTRA">Maharashtra</option>
                    {/* Add more states as needed */}
                  </select>
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Country</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    style={styles.select}
                  >
                    <option value="">Select Country</option>
                    <option value="INDIA">India</option>
                    <option value="USA">USA</option>
                    <option value="UK">UK</option>
                    {/* Add more countries as needed */}
                  </select>
                </div>
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>📞 Contact Information</h2>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Phone No</label>
                  <input
                    type="tel"
                    name="phoneNo"
                    value={formData.phoneNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Phone Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Mobile No</label>
                  <input
                    type="tel"
                    name="mobileNo"
                    value={formData.mobileNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Mobile Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Purchase Dept Mobile No</label>
                  <input
                    type="tel"
                    name="purchaseDeptMobileNo"
                    value={formData.purchaseDeptMobileNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Purchase Dept Mobile"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Fax No</label>
                  <input
                    type="text"
                    name="faxNo"
                    value={formData.faxNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Fax Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Email"
                  />
                </div>
              </div>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionHeading}>📜 Tax & License Details</h2>
              <div style={styles.twoColumnLayout}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>VAT No</label>
                  <input
                    type="text"
                    name="vatNo"
                    value={formData.vatNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter VAT Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>CST No</label>
                  <input
                    type="text"
                    name="cstNo"
                    value={formData.cstNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter CST Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>GSTIN No</label>
                  <input
                    type="text"
                    name="gstinNo"
                    value={formData.gstinNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter GSTIN Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>DL No</label>
                  <input
                    type="text"
                    name="dlNo"
                    value={formData.dlNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter DL Number"
                  />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="Enter Remarks"
                />
              </div>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionHeading}>🏦 Bank Details</h2>
              <div style={styles.twoColumnLayout}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Bank Name"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Branch Name</label>
                  <input
                    type="text"
                    name="bankBranchName"
                    value={formData.bankBranchName}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Branch Name"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Account No</label>
                  <input
                    type="text"
                    name="accountNo"
                    value={formData.accountNo}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter Account Number"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>IFSC Code</label>
                  <input
                    type="text"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter IFSC Code"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>MICR Code</label>
                  <input
                    type="text"
                    name="micrCode"
                    value={formData.micrCode}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter MICR Code"
                  />
                </div>
              </div>
            </div>

            <div style={styles.twoColumnLayout}>
              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>🖼️ Branch Image</h2>
                <div style={styles.imageUploadSection}>
                  <input
                    type="file"
                    id="imageUpload"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'image')}
                    style={styles.fileInput}
                  />
                  <label htmlFor="imageUpload" style={styles.fileInputLabel}>
                    📷 Choose Image
                  </label>
                  <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    Maximum file size: 5MB (JPEG, PNG, GIF)
                  </p>
                  {imagePreview && (
                    <div style={{ marginTop: '1rem' }}>
                      <img src={imagePreview} alt="Branch Preview" style={styles.imagePreview} />
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>📱 QR Code</h2>
                <div style={styles.imageUploadSection}>
                  <input
                    type="file"
                    id="qrCodeUpload"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'qrCode')}
                    style={styles.fileInput}
                  />
                  <label htmlFor="qrCodeUpload" style={styles.fileInputLabel}>
                    📷 Choose QR Code
                  </label>
                  <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    Maximum file size: 5MB (JPEG, PNG, GIF)
                  </p>
                  {qrCodePreview && (
                    <div style={{ marginTop: '1rem' }}>
                      <img src={qrCodePreview} alt="QR Code Preview" style={styles.imagePreview} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.buttonContainer}>
            <button
              type="button"
              onClick={handleCancel}
              style={{ ...styles.button, ...styles.cancelButton }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ ...styles.button, ...styles.submitButton }}
              disabled={submitting}
            >
              {submitting ? '⏳ Saving...' : isSaved ? 'Update Profile' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @media (max-width: 768px) {
          .twoColumnLayout {
            grid-template-columns: 1fr;
          }
          .buttonContainer {
            flex-direction: column;
          }
          .button {
            width: 100%;
            justify-content: center;
          }
        }
        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        input:hover,
        select:hover,
        textarea:hover {
          border-color: #a1a1aa;
        }
        button:hover:not(:disabled) {
          filter: brightness(90%);
        }
      `}</style>
    </div>
  );
};

export default BranchProfile;